import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchHoaMembers } from "../../../src/services/api/Member";
import PageHeader from "../Header";

/* ================= INTERFACE ================= */
interface HoaMember {
  id: number;
  name_of_association: string;
  president_name?: string | null;
  member_type?: string | null;
  name_of_sec?: string | null;
  icon?: string | null;
  multiple_images?: string[];
  position_2?: string[] | string;
}

/* ================= FLAT CARD DATA ================= */
interface CardItem {
  image: string;
  name: string;
  full_name?: string;
  member_type?: string | null;
  designation: string;
  organisation: string;
  position: string[] | string;
}

/* ================= GROUPED DATA ================= */
interface GroupedCards {
  [key: string]: CardItem[];
}

/* ================= COMPONENT ================= */
const OrganisationPage: React.FC = () => {
  const { slug } = useParams();

  const [groupedCards, setGroupedCards] = useState<GroupedCards>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const response = await fetchHoaMembers(`hoa-members/${slug}`);

        const members: HoaMember[] =
          response?.data?.data?.[0]?.hoa_members || [];

        const cardData: CardItem[] = [];

        members.forEach((item) => {
          const commonData = {
            name: item.president_name || "",
            full_name: item.president_name || "",
            member_type: item.member_type || "",
            designation: item.name_of_sec || "",
            organisation: item.name_of_association,
            position: item.position_2 || "",
          };

          /* ===== MULTIPLE IMAGES ===== */
          if (item.multiple_images?.length) {
            item.multiple_images.forEach((img) => {
              if (img) {
                cardData.push({
                  image: img,
                  ...commonData,
                });
              }
            });
          }

          /* ===== SINGLE IMAGE ===== */
          if (item.icon) {
            cardData.push({
              image: item.icon,
              ...commonData,
            });
          }
        });

        /* ================= DESIGNATION RANKING ================= */
        const designationRank: Record<string, number> = {
          president: 1,
          "secretary general": 2,
          treasurer: 3,
          "vice president": 4,
          secretary: 5,
          "joint secretary": 6,
          "executive member": 7,
          "executive members": 8,
          "joint executive member": 9,
          member: 10,
        };

        /* ================= NORMALIZE ================= */
        const normalizeDesignation = (value: string = "") =>
          value
            .toLowerCase()
            .replace(/-/g, " ")
            .replace(/\s+/g, " ")
            .trim();

        /* ================= SORT ================= */
        cardData.sort((a, b) => {
          const rankA =
            designationRank[
              normalizeDesignation(a.member_type || "")
            ] || 999;

          const rankB =
            designationRank[
              normalizeDesignation(b.member_type || "")
            ] || 999;

          return rankA - rankB;
        });

        /* ================= GROUP BY MEMBER TYPE ================= */
        const grouped: GroupedCards = {};

        cardData.forEach((card) => {
          // Skip if no image
          if (!card.image) return;

          const key = card.member_type?.trim() || "Others";

          if (!grouped[key]) {
            grouped[key] = [];
          }

          grouped[key].push(card);
        });

        setGroupedCards(grouped);
      } catch (err) {
        console.error("Error fetching organisation data:", err);
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };

    if (slug) fetchMembers();
  }, [slug]);

  /* ================= LOADING ================= */
  if (loading) {
    return (
      <div className="flex justify-center items-center h-60">
        <span className="text-gray-500">Loading...</span>
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-red-500 py-10">{error}</div>;
  }

  return (
    <div className="bg-gradient-to-b from-sky-200 to-sky-200 min-h-screen">
      <PageHeader
        title={slug?.replace(/-/g, " ") || ""}
        slug={slug}
      />

      <div className="max-w-7xl mx-auto px-2 py-8 align-middle">
        {Object.entries(groupedCards).map(([heading, cards]) => {
          // Don't show heading if no cards
          if (!cards.length) return null;

          return (
            <div key={heading} className="mb-12">
              {/* ================= HEADING ================= */}
              <h2 className="text-3xl font-bold text-start text-blue-900 uppercase mb-8">
                {heading}
              </h2>

              {/* ================= CARDS ================= */}
              <div
                className={`grid gap-6 ${
                  cards.length === 1
                    ? "grid-cols-1 place-items-center border bg-gray-600"
                    : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 border-2 bg-gray-400 px-2 py-4 rounded-xl place-items-center"
                }`}
              >
                {cards.map((card, index) => (
                  <div
                    key={index}
                    className={`rounded-xl overflow-hidden bg-white shadow hover:shadow-lg transition w-full ${
                      cards.length === 1 ? "max-w-sm" : ""
                    }`}
                  >
                    {/* IMAGE */}
                    {card.image && (
                      <img
                        src={card.image}
                        alt={card.name}
                        className="w-full h-60 object-contain"
                      />
                    )}

                    {/* TEXT */}
                    <div className="py-3 px-4 text-center bg-gradient-to-r from-blue-900 to-indigo-900 text-white h-full">
                    <h4 className="font-semibold text-xl">
                      {(card.full_name || card.name)
                        ?.toLowerCase()
                        .replace(/\b\w/g, (char) => char.toUpperCase())
                        .replace(/\bIas\b/g, "IAS")
                        .replace(/\bMla\b/g, "MLA")}
                    </h4>
                      <div className="flex flex-col items-center mt-1">
                        {Array.isArray(card.position)
                          ? card.position.map((pos, idx) => (
                              <React.Fragment key={idx}>
                                <p className="text-base italic ">
                                  {pos}
                                  {card.position.length > 1 &&
                                    (idx < card.position.length - 1 ? ", " : ".")}
                                </p>
                              </React.Fragment>
                            ))
                          : (
                            <p className="text-sm italic mt-1">
                              {card.position}
                            </p>
                          )}
                      </div>
                  </div>
              </div>
                ))}
              </div>

            </div>
          );
        })}

        {/* ================= EMPTY ================= */}
        {Object.keys(groupedCards).length === 0 && (
          <div className="text-center text-gray-500 mt-10">
            No data available
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganisationPage;